import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { WhatsappInstance, WhatsappGroup, WhatsappChannel, WhatsappBroadcastHistory } from '@/types'

// ─── Instance ────────────────────────────────────────────

export function useWhatsappStatus() {
  return useQuery<WhatsappInstance>({
    queryKey: ['whatsapp', 'status'],
    queryFn: () => api.get('/whatsapp/instance/status'),
  })
}

export function useCreateInstance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/whatsapp/instance'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  })
}

export function useConnectInstance() {
  const qc = useQueryClient()
  return useMutation<{ qrCode: string | null; status: string }>({
    mutationFn: () => api.post('/whatsapp/instance/connect'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'status'] }),
  })
}

export function useDisconnectInstance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/whatsapp/instance/disconnect'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  })
}

export function useRestartInstance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/whatsapp/instance/restart'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  })
}

export function useDeleteInstance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/whatsapp/instance'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  })
}

// ─── Groups ──────────────────────────────────────────────

export function useWhatsappGroups(enabled = true) {
  return useQuery<WhatsappGroup[]>({
    queryKey: ['whatsapp', 'groups'],
    queryFn: () => api.get('/whatsapp/groups?participants=true'),
    enabled,
  })
}

export function useWhatsappGroupInfo(groupJid: string | null) {
  return useQuery<WhatsappGroup>({
    queryKey: ['whatsapp', 'group', groupJid],
    queryFn: () => api.get(`/whatsapp/groups/${encodeURIComponent(groupJid!)}`),
    enabled: !!groupJid,
  })
}

export function useCreateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { subject: string; description?: string; participants: string[] }) =>
      api.post('/whatsapp/groups', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useGroupInviteCode() {
  return useMutation<{ inviteCode?: string; inviteUrl?: string }, Error, string>({
    mutationFn: (groupJid: string) =>
      api.get(`/whatsapp/groups/${encodeURIComponent(groupJid)}/invite-code`),
  })
}

export function useRevokeInviteCode() {
  return useMutation({
    mutationFn: (groupJid: string) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/revoke-invite`),
  })
}

export function useAddParticipants() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, participants }: { groupJid: string; participants: string[] }) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/participants`, { participants }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useRemoveParticipants() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, participants }: { groupJid: string; participants: string[] }) =>
      api.delete(`/whatsapp/groups/${encodeURIComponent(groupJid)}/participants`, { participants }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function usePromoteParticipants() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, participants }: { groupJid: string; participants: string[] }) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/promote`, { participants }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useDemoteParticipants() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, participants }: { groupJid: string; participants: string[] }) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/demote`, { participants }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useUpdateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, ...data }: { groupJid: string; subject?: string; description?: string; pictureUrl?: string }) =>
      api.put(`/whatsapp/groups/${encodeURIComponent(groupJid)}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useUpdateGroupSetting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, action }: { groupJid: string; action: string }) =>
      api.put(`/whatsapp/groups/${encodeURIComponent(groupJid)}/settings`, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useLeaveGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (groupJid: string) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/leave`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useToggleEphemeral() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupJid, expiration }: { groupJid: string; expiration: number }) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/ephemeral`, { expiration }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'groups'] }),
  })
}

export function useSendGroupInvite() {
  return useMutation({
    mutationFn: ({ groupJid, numbers }: { groupJid: string; numbers: string[] }) =>
      api.post(`/whatsapp/groups/${encodeURIComponent(groupJid)}/send-invite`, { numbers }),
  })
}

// ─── Channels ────────────────────────────────────────────

export function useWhatsappChannels(enabled = true) {
  return useQuery<WhatsappChannel[]>({
    queryKey: ['whatsapp', 'channels'],
    queryFn: () => api.get('/whatsapp/channels'),
    enabled,
  })
}

export function useSendToChannel() {
  return useMutation({
    mutationFn: (data: { channelJid: string; text: string }) =>
      api.post('/whatsapp/channels/send', data),
  })
}

// ─── Broadcast ────────────────────────────────────────────

export function useWhatsappBroadcastHistory(page = 1) {
  return useQuery<{ data: WhatsappBroadcastHistory[]; last_page: number; current_page: number }>({
    queryKey: ['whatsapp', 'broadcast-history', page],
    queryFn: () => api.get(`/whatsapp/broadcast/history?page=${page}`),
  })
}

export function useDeleteWhatsappBroadcast() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/whatsapp/broadcast/history/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'broadcast-history'] }),
  })
}

export function useCancelWhatsappBroadcast() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/whatsapp/broadcast/cancel/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'broadcast-history'] }),
  })
}
